// Pure helpers for the per-step `thread_reply` flag (reply-in-thread vs new
// thread). Kept out of the WizardSequenceStep component so the write-emit and
// hydration rules are unit-testable without rendering the whole builder.
//
// Invariant: the first non-variant step (the thread starter — minimum order)
// always emits `thread_reply=false`. Variants inherit their original's slot, so
// they are never the thread starter. The backend enforces the same rule (see
// server/emailbison/clients/types.py::enforce_first_step_starts_thread); forcing
// it here keeps the payload honest and matches the no-toggle-on-step-1 UI.

interface ThreadReplyStep {
  is_variant?: boolean
  thread_reply?: boolean
}

/**
 * Index of the first non-variant step (the thread starter). Returns -1 when
 * every step is a variant (the authoring UI never produces this, but the helper
 * degrades safely).
 */
export function firstNonVariantIndex(steps: ThreadReplyStep[]): number {
  return steps.findIndex((step) => !step.is_variant)
}

/**
 * The `thread_reply` value to emit for a step at `index`. The first non-variant
 * slot is forced to `false`; every other step emits its authored value
 * (defaulting to `false` when unset).
 */
export function resolveThreadReplyForEmit(
  steps: ThreadReplyStep[],
  index: number
): boolean {
  const firstSlotIndex = firstNonVariantIndex(steps)
  if (index === firstSlotIndex) return false
  return steps[index]?.thread_reply ?? false
}

/**
 * The `thread_reply` value to hydrate into the form from a stored step. Stored
 * values render verbatim — pre-feature steps (undefined) hydrate as `false`, so
 * opening and saving an old sequence never silently rethreads it. Default-on is
 * applied only to newly added steps, not on hydration.
 *
 * NOTE — the `undefined` default is intentionally INVERTED relative to
 * `isThreadReplyOn`, because the two readers answer different questions:
 *   - `hydrateThreadReply(undefined)` → `false` (storage: an absent stored value
 *     is a legacy step that keeps its current, un-threaded behavior).
 *   - `isThreadReplyOn(undefined)`    → `true`  (live UI display: a freshly added
 *     step the user hasn't touched shows the toggle on).
 * Use `hydrateThreadReply` when mapping a persisted step into the form; never
 * substitute `isThreadReplyOn` there — it would silently flip legacy steps on.
 */
export function hydrateThreadReply(storedValue: boolean | undefined): boolean {
  return storedValue ?? false
}

/**
 * Whether a step is "reply in thread". The toggle defaults on for every step
 * after the first, so an unset (`undefined`) value reads as `true`. Mirrors the
 * `field.thread_reply ?? true` default used by the Switch control.
 *
 * This is a DISPLAY default and is the inverse of `hydrateThreadReply`'s
 * storage default — see that function's note before reusing this in any
 * hydration/persistence path.
 */
export function isThreadReplyOn(step: ThreadReplyStep): boolean {
  return step.thread_reply ?? true
}

/**
 * Whether the auto-`Re:` subject cascade from step 1 should overwrite the step
 * at `index`. A step is skipped when it is step 1 itself, a variant (variants
 * own their subject), or has reply-to-thread turned **off** — an off step starts
 * a new thread with its own real subject, so it must not be clobbered with
 * `Re: {step 1 subject}`.
 */
export function shouldCascadeSubjectToStep(
  steps: ThreadReplyStep[],
  index: number
): boolean {
  const firstSlotIndex = firstNonVariantIndex(steps)
  if (index === firstSlotIndex) return false
  const step = steps[index]
  if (!step) return false
  if (step.is_variant) return false
  return isThreadReplyOn(step)
}

/**
 * The subject a step should keep when reply-to-thread is turned **off**. An off
 * step starts a new thread with its own subject, so any inherited `Re: ...` the
 * cascade auto-populated while it was on is stale and must be cleared (→ `''`,
 * exposing the required-subject input + placeholder). A subject the user typed
 * themselves (no `Re: ` prefix) is preserved verbatim.
 */
export function clearInheritedSubjectOnThreadReplyOff(
  currentSubject: string | undefined
): string {
  const subject = currentSubject ?? ''
  return subject.startsWith('Re: ') ? '' : subject
}

/** Strips every leading `Re: ` from a subject so it can be (re)prefixed exactly
 * once. `"Re: Re: Foo"` → `"Foo"`, `"Foo"` → `"Foo"`, `"Re: "` → `""`. A
 * mid-string `Re:` is left intact. */
export function stripReplyPrefix(subject: string | undefined): string {
  return (subject ?? '').replace(/^(?:Re:\s+)+/, '')
}

/**
 * The cosmetic inherited subject for a step replying within step 1's thread:
 * exactly one `Re: ` in front of step 1's subject. Any leading `Re: ` chain on
 * the base is collapsed first, so a step-1 subject that is itself a reply (e.g.
 * `Re: Test`) yields `Re: Test`, never `Re: Re: Test`. A blank base (or one
 * that is only `Re: ` prefixes) yields `''`. Single source of truth for the
 * three sites that derive a reply subject (`addStep`, the step-1 cascade in
 * `handlePrimarySubjectChange`, and `applyInheritedSubjectOnThreadReplyOn`).
 */
export function inheritedReplySubject(
  firstStepSubject: string | undefined
): string {
  const base = stripReplyPrefix(firstStepSubject)
  return base.trim() ? `Re: ${base}` : ''
}

/**
 * The subject to PERSIST for a step, given the value to emit for its
 * `thread_reply` flag. EmailBison prepends its own `Re: ` to every reply-in-
 * thread step, so the cosmetic `Re: ` the builder shows must be stripped before
 * saving — otherwise the stored subject doubles to `Re: Re: ...` on every save.
 * Non-reply steps (step 1, new-thread steps) keep their subject verbatim, since
 * EmailBison does not touch their subject.
 */
export function subjectForEmit(
  subject: string | undefined,
  threadReplyOnEmit: boolean
): string {
  const value = subject ?? ''
  return threadReplyOnEmit ? stripReplyPrefix(value) : value
}

/**
 * The subject a step should adopt when reply-to-thread is turned **on**. An on
 * step replies within step 1's thread, so it inherits the cosmetic
 * `Re: {step 1 subject}` — the same value `addStep()` seeds and
 * `handlePrimarySubjectChange()` cascades. This is the inverse of
 * `clearInheritedSubjectOnThreadReplyOff`: only an empty subject or one that is
 * already an inherited `Re: ...` is (re)filled, so a fresh subject the user
 * authored while the step was off is preserved verbatim. When step 1 has no
 * subject yet, the result is `''` (exposing the placeholder until step 1 is
 * filled, at which point the cascade populates it).
 */
export function applyInheritedSubjectOnThreadReplyOn(
  currentSubject: string | undefined,
  firstStepSubject: string | undefined
): string {
  const subject = currentSubject ?? ''
  // Preserve a fresh, user-authored subject — only (re)fill an empty or
  // already-inherited "Re: ..." slot. Known tradeoff: a subject the user typed
  // that literally begins with "Re: " is indistinguishable from a cascade-
  // inherited one, so toggling reply-on overwrites it with the step-1 reply
  // subject. Accepted as a rare edge case — the cosmetic prefix is the cascade's
  // only marker, and EmailBison strips/re-adds "Re: " on reply steps anyway.
  if (subject.trim() !== '' && !subject.startsWith('Re: ')) return subject
  return inheritedReplySubject(firstStepSubject)
}
